/* ============================================================
 * HUB Workwell · Gráficos de fisioterapia (componente partilhado)
 *
 * Usado em dois sítios, para os números nunca divergirem:
 *   · a página autónoma  src/pages/DashboardFisio.jsx
 *   · a secção do portal src/components/SeccaoFisioPortal.jsx
 *
 * Recebe os dados já agregados pela base e só os desenha.
 * Guardar em src/components/FisioGraficos.jsx
 * ============================================================ */

import React from 'react';

/* ---------- identidade Workwell ---------- */
const ROXO = '#691FB1';
const ROXO_ESC = '#4A1580';
const ROXO_MED = '#736CCE';
const ROSA = '#FF357B';
const ROSA_ESC = '#BE1356';
const AZUL = '#57B1DD';
const AZUL_ESC = '#2C6EAF';
const RAMPA = ['#F4F0FA', '#E0D6F2', '#BBA1E2', '#9466CC', '#691FB1', '#4A1580'];
const CAT = [ROXO, ROSA, AZUL, AZUL_ESC];
const TINTA = '#1E1A2B', TINTA2 = '#55506B', TINTA3 = '#8A85A0';
const LINHA = '#E7E4EE', GRELHA = '#EFEDF4';

const MESES = ['janeiro','fevereiro','março','abril','maio','junho',
               'julho','agosto','setembro','outubro','novembro','dezembro'];
const ZONAS = ['Coluna cervical','Coluna dorsal','Coluna lombar','Ombros','Cotovelos',
               'Punhos/mãos','Coxas','Joelhos','Pernas','Tornozelos/pés'];

const nf = new Intl.NumberFormat('pt-PT');
const nf1 = new Intl.NumberFormat('pt-PT', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

function rampa(t) {
  t = Math.max(0, Math.min(1, isFinite(t) ? t : 0));
  const p = t * (RAMPA.length - 1), i = Math.floor(p), f = p - i;
  const hx = h => [1, 3, 5].map(k => parseInt(h.slice(k, k + 2), 16));
  const A = hx(RAMPA[i]), B = hx(RAMPA[Math.min(i + 1, RAMPA.length - 1)]);
  return '#' + A.map((v, k) => Math.round(v + (B[k] - v) * f).toString(16).padStart(2, '0')).join('');
}

/* ---------- silhueta ---------- */
const SILHUETA = [
  ['ellipse', { cx: 105, cy: 28, rx: 19, ry: 22 }],
  ['rect', { x: 96, y: 42, width: 18, height: 20, rx: 6 }],
  ['rect', { x: 78, y: 56, width: 54, height: 92, rx: 18 }],
  ['rect', { x: 82, y: 132, width: 46, height: 42, rx: 14 }],
  ['circle', { cx: 82, cy: 68, r: 14 }], ['circle', { cx: 128, cy: 68, r: 14 }],
  ['rect', { x: 59, y: 62, width: 17, height: 58, rx: 8 }], ['rect', { x: 134, y: 62, width: 17, height: 58, rx: 8 }],
  ['circle', { cx: 67, cy: 117, r: 9 }], ['circle', { cx: 143, cy: 117, r: 9 }],
  ['rect', { x: 52, y: 112, width: 16, height: 52, rx: 8 }], ['rect', { x: 142, y: 112, width: 16, height: 52, rx: 8 }],
  ['ellipse', { cx: 59, cy: 172, rx: 10, ry: 13 }], ['ellipse', { cx: 151, cy: 172, rx: 10, ry: 13 }],
  ['rect', { x: 83, y: 160, width: 21, height: 62, rx: 10 }], ['rect', { x: 106, y: 160, width: 21, height: 62, rx: 10 }],
  ['circle', { cx: 93, cy: 224, r: 11 }], ['circle', { cx: 117, cy: 224, r: 11 }],
  ['rect', { x: 85, y: 220, width: 17, height: 56, rx: 8 }], ['rect', { x: 108, y: 220, width: 17, height: 56, rx: 8 }],
  ['ellipse', { cx: 92, cy: 282, rx: 12, ry: 9 }], ['ellipse', { cx: 118, cy: 282, rx: 12, ry: 9 }],
];
const ZONA_FORMAS = {
  'Coluna cervical': [['rect', { x: 88, y: 40, width: 34, height: 20 }]],
  'Ombros': [['rect', { x: 56, y: 54, width: 32, height: 26 }], ['rect', { x: 122, y: 54, width: 32, height: 26 }]],
  'Coluna dorsal': [['rect', { x: 88, y: 60, width: 34, height: 46 }]],
  'Coluna lombar': [['rect', { x: 88, y: 106, width: 34, height: 48 }]],
  'Cotovelos': [['rect', { x: 46, y: 104, width: 28, height: 24 }], ['rect', { x: 136, y: 104, width: 28, height: 24 }]],
  'Punhos/mãos': [['rect', { x: 42, y: 150, width: 32, height: 40 }], ['rect', { x: 136, y: 150, width: 32, height: 40 }]],
  'Coxas': [['rect', { x: 76, y: 158, width: 28, height: 56 }], ['rect', { x: 106, y: 158, width: 28, height: 56 }]],
  'Joelhos': [['rect', { x: 78, y: 214, width: 28, height: 22 }], ['rect', { x: 104, y: 214, width: 28, height: 22 }]],
  'Pernas': [['rect', { x: 78, y: 236, width: 28, height: 38 }], ['rect', { x: 104, y: 236, width: 28, height: 38 }]],
  'Tornozelos/pés': [['rect', { x: 76, y: 270, width: 32, height: 26 }], ['rect', { x: 102, y: 270, width: 32, height: 26 }]],
};
const Forma = ({ t, a, children, ...rest }) => React.createElement(t, { ...a, ...rest }, children);

/* ---------- peças ---------- */
const Cartao = ({ titulo, sub, children, style, className }) => (
  <div className={className} style={{ background: '#fff', border: `1px solid ${LINHA}`, borderRadius: 14, padding: '16px 18px', minWidth: 0, ...style }}>
    {titulo && <h3 style={{ margin: 0, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.09em', color: TINTA3, fontWeight: 700 }}>{titulo}</h3>}
    {sub && <div style={{ fontSize: 11.5, color: TINTA3, marginBottom: 10 }}>{sub}</div>}
    {children}
  </div>
);

const Vazio = ({ texto = 'Sem registos no período' }) => (
  <div style={{ color: TINTA3, fontSize: 13, padding: '22px 0', textAlign: 'center' }}>{texto}</div>
);

function Barras({ itens, unidade = 'sessões' }) {
  if (!itens.length || itens.every(d => !d.v)) return <Vazio />;
  const alturaLinha = 26, padT = 6, padB = 20;
  const largLabel = Math.min(128, Math.max(...itens.map(d => d.k.length)) * 6.4 + 8);
  const W = 520, H = padT + itens.length * alturaLinha + padB, x0 = largLabel + 8, x1 = W - 46;
  const max = Math.max(...itens.map(d => d.v)) || 1;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', overflow: 'visible' }}>
      {[0, .5, 1].map((t, i) => {
        const x = x0 + (x1 - x0) * t;
        return (
          <g key={i}>
            <line x1={x} x2={x} y1={padT} y2={H - padB} stroke={GRELHA} />
            <text x={x} y={H - padB + 13} fontSize={10.5} fill={TINTA3} textAnchor="middle">{nf.format(Math.round(max * t))}</text>
          </g>
        );
      })}
      {itens.map((d, i) => {
        const y = padT + i * alturaLinha, bw = Math.max(d.v > 0 ? 3 : 0, (x1 - x0) * (d.v / max));
        return (
          <g key={d.k}>
            <text x={largLabel} y={y + 15} fontSize={10.5} fill={TINTA2} textAnchor="end">{d.k}</text>
            <rect x={x0} y={y + 4} width={bw} height={14} rx={4} fill={rampa(.35 + .65 * (d.v / max))}>
              <title>{`${d.k}: ${nf.format(d.v)} ${unidade}`}</title>
            </rect>
            <text x={x0 + bw + 6} y={y + 15} fontSize={11} fontWeight={700} fill={TINTA2}>{nf.format(d.v)}</text>
          </g>
        );
      })}
    </svg>
  );
}

function MapaCorporal({ zonas }) {
  const contagem = {}; ZONAS.forEach(z => (contagem[z] = 0));
  let total = 0;
  zonas.forEach(({ zona, sessoes }) => { if (contagem[zona] !== undefined) { contagem[zona] = sessoes; total += sessoes; } });
  const max = Math.max(1, ...ZONAS.map(z => contagem[z]));
  const ESQ = ['Ombros', 'Cotovelos', 'Punhos/mãos', 'Coxas', 'Tornozelos/pés'];

  const etiqueta = z => {
    const v = contagem[z], p = total ? v / total : 0;
    return (
      <div key={z} style={{ display: 'flex', alignItems: 'center', gap: 6, border: `1px solid ${LINHA}`,
        borderRadius: 8, padding: '5px 8px', fontSize: 11.5, background: '#fff', minWidth: 0, maxWidth: '100%' }}>
        <span style={{ width: 9, height: 9, borderRadius: 2, flex: 'none', background: v ? rampa(.18 + .82 * (v / max)) : '#EDEAF3' }} />
        {z} <b style={{ fontSize: 12.5 }}>{nf1.format(p * 100)}%</b>
      </div>
    );
  };

  return (
    <div className="wwfg-corpo">
      <div className="wwfg-zlista" style={{ alignItems: 'flex-end' }}>
        {ZONAS.filter(z => ESQ.includes(z)).map(etiqueta)}
      </div>
      <svg viewBox="0 0 210 300" style={{ width: '100%', height: 'auto' }}>
        <defs>
          <clipPath id="corpo">{SILHUETA.map(([t, a], i) => <Forma key={i} t={t} a={a} />)}</clipPath>
        </defs>
        <g fill="#EFECF5">{SILHUETA.map(([t, a], i) => <Forma key={i} t={t} a={a} />)}</g>
        <g clipPath="url(#corpo)">
          {ZONAS.map(z => {
            const v = contagem[z]; if (!v) return null;
            const cor = rampa(.2 + .8 * (v / max));
            return (ZONA_FORMAS[z] || []).map(([t, a], i) => (
              <Forma key={z + i} t={t} a={a} style={{ fill: cor }} stroke="#fff" strokeWidth={1}>
                <title>{`${z}: ${nf.format(v)} sessões · ${nf1.format((v / (total || 1)) * 100)}%`}</title>
              </Forma>
            ));
          })}
        </g>
      </svg>
      <div className="wwfg-zlista" style={{ alignItems: 'flex-start' }}>
        {ZONAS.filter(z => !ESQ.includes(z)).map(etiqueta)}
      </div>
    </div>
  );
}

function Intensidade({ dados }) {
  if (!dados || !dados.n) return <Vazio />;
  const { inicio, fim, n } = dados;
  const reducao = dados.reducao != null
    ? dados.reducao
    : (inicio ? ((inicio - fim) / inicio) * 100 : 0);
  return (
    <div>
      <style>{ESTILO}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.07em', color: TINTA3, fontWeight: 700 }}>Início</div>
          <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-.03em', color: ROSA_ESC }}>{nf1.format(inicio)}</div>
        </div>
        <div style={{ fontSize: 20, color: TINTA3 }}>→</div>
        <div>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.07em', color: TINTA3, fontWeight: 700 }}>Fim</div>
          <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-.03em', color: ROXO }}>{nf1.format(fim)}</div>
        </div>
        <div style={{ background: '#EAF7EF', color: '#1D7A46', borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 700 }}>
          −{nf1.format(reducao)}% de dor
        </div>
      </div>
      <svg viewBox="0 0 300 54" style={{ width: '100%', height: 'auto', marginTop: 8 }}>
        {[[inicio, ROSA_ESC, 'Início'], [fim, ROXO, 'Fim']].map(([v, cor, rot], i) => (
          <g key={rot}>
            <rect x={52} y={i * 26 + 4} width={230} height={12} rx={4} fill="#F1EFF6" />
            <rect x={52} y={i * 26 + 4} width={Math.max(3, 230 * (v / 10))} height={12} rx={4} fill={cor} />
            <text x={46} y={i * 26 + 14} fontSize={10.5} fill={TINTA2} textAnchor="end">{rot}</text>
          </g>
        ))}
        <text x={52} y={52} fontSize={10.5} fill={TINTA3}>0</text>
        <text x={282} y={52} fontSize={10.5} fill={TINTA3} textAnchor="end">10</text>
      </svg>
      <div style={{ fontSize: 11.5, color: TINTA3, marginTop: 8 }}>
        Base: {nf.format(n)} consultas com registo antes e depois
      </div>
    </div>
  );
}

function Genero({ dados }) {
  if (!dados.length) return <Vazio />;
  const total = dados.reduce((a, d) => a + d.sessoes, 0);
  const cores = { Feminino: ROXO, Masculino: ROSA };
  let x = 0;
  return (
    <div>
      <svg viewBox="0 0 340 26" style={{ width: '100%', height: 'auto' }}>
        {dados.map((d, i) => {
          const w = 340 * (d.sessoes / total), cor = cores[d.genero] || CAT[i % CAT.length];
          const r = <rect key={d.genero} x={x + (i ? 1 : 0)} y={0} width={Math.max(2, w - (i ? 1 : 0))} height={22} rx={4} fill={cor} />;
          x += w; return r;
        })}
      </svg>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11.5, color: TINTA2, marginTop: 8 }}>
        {dados.map((d, i) => (
          <span key={d.genero}>
            <i style={{ width: 10, height: 10, borderRadius: 3, display: 'inline-block', marginRight: 5,
              background: cores[d.genero] || CAT[i % CAT.length] }} />
            {d.genero} · <b>{nf1.format((d.sessoes / total) * 100)}%</b>
          </span>
        ))}
      </div>
    </div>
  );
}

function ConsultasPorMes({ mensal }) {
  if (!mensal.length) return <Vazio />;
  const anos = [...new Set(mensal.map(d => d.ano))].sort();
  const series = anos.map((ano, i) => {
    const vals = Array(12).fill(null);
    mensal.filter(d => d.ano === ano).forEach(d => { vals[d.mes - 1] = d.sessoes; });
    const ativos = vals.map((v, k) => (v != null ? k : -1)).filter(k => k >= 0);
    const lo = Math.min(...ativos), hi = Math.max(...ativos);
    for (let k = lo; k <= hi; k++) if (vals[k] == null) vals[k] = 0;
    return { ano, cor: CAT[i % CAT.length], vals, lo, hi };
  });
  const max = Math.max(1, ...series.flatMap(s => s.vals.filter(v => v != null)));
  const W = 760, H = 250, L = 36, R = 44, T = 10, B = 34;
  const x = i => L + (W - L - R) * (i / 11), y = v => T + (H - T - B) * (1 - v / max);
  const usados = [];
  return (
    <div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11.5, color: TINTA2, margin: '2px 0 8px' }}>
        {series.map(s => (
          <span key={s.ano}><i style={{ width: 10, height: 10, borderRadius: 3, display: 'inline-block', marginRight: 5, background: s.cor }} />{s.ano}</span>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', overflow: 'visible' }}>
        {[0, 1, 2, 3, 4].map(t => {
          const v = (max * t) / 4;
          return (<g key={t}>
            <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke={GRELHA} />
            <text x={L - 8} y={y(v) + 4} fontSize={10.5} fill={TINTA3} textAnchor="end">{nf.format(Math.round(v))}</text>
          </g>);
        })}
        {MESES.map((m, i) => <text key={m} x={x(i)} y={H - B + 16} fontSize={10.5} fill={TINTA3} textAnchor="middle">{m.slice(0, 3)}</text>)}
        {series.map(s => {
          let d = '', aberto = false;
          s.vals.forEach((v, i) => {
            if (v == null) { aberto = false; return; }
            d += `${aberto ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)} `; aberto = true;
          });
          let ly = y(s.vals[s.hi]);
          while (usados.some(u => Math.abs(u - ly) < 11)) ly += 12;
          usados.push(ly);
          return (
            <g key={s.ano}>
              <path d={d} fill="none" stroke={s.cor} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
              {s.vals.map((v, i) => v == null ? null : (
                <circle key={i} cx={x(i)} cy={y(v)} r={4} fill={s.cor} stroke="#fff" strokeWidth={2}>
                  <title>{`${s.ano} · ${MESES[i]}: ${nf.format(v)} consultas`}</title>
                </circle>
              ))}
              <text x={Math.min(x(s.hi) + 8, W - R + 4)} y={ly + 4} fontSize={11} fontWeight={700} fill={s.cor}>{s.ano}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function Heatmap({ heatmap }) {
  if (!heatmap.length) return <Vazio />;
  const m = {}; ZONAS.forEach(z => (m[z] = Array(12).fill(0)));
  heatmap.forEach(({ zona, mes, sessoes }) => { if (m[zona]) m[zona][mes - 1] = sessoes; });
  const zonas = ZONAS.filter(z => m[z].some(v => v > 0));
  if (!zonas.length) return <Vazio texto="Sem zonas de dor registadas" />;
  const max = Math.max(...zonas.flatMap(z => m[z]));
  const L = 124, cw = 48, ch = 26, T = 22;
  const W = L + 12 * cw + 10, H = T + zonas.length * ch + 16;
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
        {MESES.map((mm, i) => <text key={mm} x={L + i * cw + cw / 2} y={14} fontSize={10.5} fill={TINTA3} textAnchor="middle">{mm.slice(0, 3)}</text>)}
        {zonas.map((z, r) => (
          <g key={z}>
            <text x={L - 8} y={T + r * ch + 17} fontSize={10.5} fill={TINTA2} textAnchor="end">{z}</text>
            {m[z].map((v, i) => (
              <g key={i}>
                <rect x={L + i * cw + 1} y={T + r * ch + 1} width={cw - 2} height={ch - 2} rx={4}
                      fill={v ? rampa(.15 + .85 * (v / max)) : '#F7F6FA'}>
                  <title>{`${z} · ${MESES[i]}: ${nf.format(v)} sessões`}</title>
                </rect>
                {v > 0 && (
                  <text x={L + i * cw + cw / 2} y={T + r * ch + 17} fontSize={11} fontWeight={700}
                        textAnchor="middle" fill={v / max > .55 ? '#fff' : TINTA2}>{nf.format(v)}</text>
                )}
              </g>
            ))}
          </g>
        ))}
      </svg>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11.5, color: TINTA2, marginTop: 8 }}>
        escala
        {[0, .25, .5, .75, 1].map(t => <i key={t} style={{ width: 10, height: 10, borderRadius: 3, display: 'inline-block', background: rampa(.15 + .85 * t) }} />)}
        0 → {nf.format(max)} sessões
      </div>
    </div>
  );
}

function Ginastica({ dados }) {
  if (!dados.length) return <Vazio texto="Sem sessões de ginástica laboral no período" />;
  const vals = Array(12).fill(null), ses = Array(12).fill(0);
  dados.forEach(d => { vals[d.mes - 1] = d.participantes; ses[d.mes - 1] = d.sessoes; });
  const ativos = vals.map((v, i) => (v != null ? i : -1)).filter(i => i >= 0);
  const lo = Math.min(...ativos), hi = Math.max(...ativos);
  for (let i = lo; i <= hi; i++) if (vals[i] == null) vals[i] = 0;
  const max = Math.max(1, ...vals.filter(v => v != null));
  const W = 1000, H = 210, L = 42, R = 16, T = 10, B = 30;
  const x = i => L + (W - L - R) * (i / 11), y = v => T + (H - T - B) * (1 - v / max);
  const pts = []; for (let i = lo; i <= hi; i++) pts.push([i, vals[i]]);
  const linha = pts.map(([i, v], k) => `${k ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const total = vals.reduce((a, b) => a + (b || 0), 0);
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
        {[0, 1, 2, 3, 4].map(t => {
          const v = (max * t) / 4;
          return (<g key={t}>
            <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke={GRELHA} />
            <text x={L - 8} y={y(v) + 4} fontSize={10.5} fill={TINTA3} textAnchor="end">{nf.format(Math.round(v))}</text>
          </g>);
        })}
        {MESES.map((m, i) => (
          <text key={m} x={x(i)} y={H - B + 16} fontSize={10.5} fill={TINTA3} textAnchor="middle" opacity={i < lo || i > hi ? .45 : 1}>{m.slice(0, 3)}</text>
        ))}
        <path d={`M${x(lo)},${y(0)} ` + linha.slice(1) + ` L${x(hi)},${y(0)} Z`} fill={ROXO} fillOpacity={.08} />
        <path d={linha} fill="none" stroke={ROXO} strokeWidth={2} strokeLinejoin="round" />
        {pts.map(([i, v]) => (
          <circle key={i} cx={x(i)} cy={y(v)} r={4.5} fill={ROXO} stroke="#fff" strokeWidth={2}>
            <title>{`${MESES[i]}: ${nf.format(v)} participantes · ${nf.format(ses[i])} sessões`}</title>
          </circle>
        ))}
      </svg>
      <div style={{ fontSize: 11.5, color: TINTA3, marginTop: 6 }}>
        Total no período: <b>{nf.format(total)}</b> participantes
      </div>
    </div>
  );
}



const ESTILO = `
.wwfg-g12{display:grid;gap:14px;grid-template-columns:repeat(12,1fr);align-items:start;margin-top:14px}
.wwfg-kpis{display:grid;gap:14px;grid-template-columns:repeat(auto-fit,minmax(168px,1fr));margin-top:14px}
.wwfg-s4{grid-column:span 4}.wwfg-s5{grid-column:span 5}
.wwfg-s7{grid-column:span 7}.wwfg-s12{grid-column:span 12}
@media (max-width:980px){.wwfg-s4,.wwfg-s5,.wwfg-s7{grid-column:span 12}}
.wwfg-corpo{display:grid;grid-template-columns:minmax(0,1fr) minmax(150px,200px) minmax(0,1fr);gap:8px;align-items:center}
.wwfg-corpo>*{min-width:0}
.wwfg-zlista{display:flex;flex-direction:column;gap:6px;min-width:0}
.wwfg-zlista>div{max-width:100%}
@media (max-width:640px){.wwfg-corpo{grid-template-columns:1fr}.wwfg-zlista{align-items:stretch!important}}
@media print{.wwfg-filtros{display:none!important}.wwfg-g12>*{break-inside:avoid}}
`;

/* ============================================================
 * O conjunto: filtros, KPIs e todos os cartões
 * ============================================================ */
export default function FisioGraficos({ dados, filtros, onFiltros, aCarregar }) {
  if (!dados) return null;

  const k = dados.kpis || {};

  // O total só aparece quando há mais do que fisioterapia no cliente —
  // de outro modo repetia o cartão seguinte.
  const total = k.sessoes_total ?? k.sessoes_fisio ?? 0;
  const varios = total > (k.sessoes_fisio || 0);

  const kpis = [];
  if (varios) kpis.push({ v: total, c: 'Sessões realizadas', todos: true });
  kpis.push(
    { v: k.sessoes_fisio, c: 'Consultas de fisioterapia' },
    { v: k.colaboradores, c: 'Colaboradores intervencionados' },
    { v: k.em_tratamento, c: 'Colaboradores em tratamento', rosa: true },
    { v: k.altas, c: 'Colaboradores com alta' },
  );
  if (k.sessoes_gl) {
    kpis.push({
      v: k.sessoes_gl,
      c: `Sessões de ginástica laboral${k.participantes_gl ? ` · ${nf.format(k.participantes_gl)} participantes` : ''}`,
    });
  }
  if (k.sessoes_coaching) kpis.push({ v: k.sessoes_coaching, c: 'Sessões de coaching postural' });

  const sel = {
    padding: '8px 10px', border: `1px solid ${LINHA}`, borderRadius: 9,
    fontSize: 13, color: TINTA, background: '#fff', cursor: 'pointer', minWidth: 140,
  };
  const rotulo = { fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.08em', color: TINTA3, fontWeight: 700, display: 'block', marginBottom: 4 };


  return (
    <div>
<div className="wwfg-filtros" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', margin: '18px 0 6px' }}>
          <div>
            <label style={rotulo} htmlFor="fAno">Ano</label>
            <select id="fAno" style={sel} value={filtros.ano} onChange={e => onFiltros({ ...filtros, ano: e.target.value, mes: '' })}>
              <option value="">Todos os anos</option>
              {(dados.filtros?.anos || []).map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label style={rotulo} htmlFor="fMes">Mês</label>
            <select id="fMes" style={sel} value={filtros.mes} onChange={e => onFiltros({ ...filtros, mes: e.target.value })}>
              <option value="">Todos os meses</option>
              {(dados.filtros?.meses || []).map(m => <option key={m} value={m}>{MESES[m - 1]}</option>)}
            </select>
          </div>
          {(dados.filtros?.locais || []).length > 0 && (
            <div>
              <label style={rotulo} htmlFor="fLocal">Localização</label>
              <select id="fLocal" style={sel} value={filtros.local} onChange={e => onFiltros({ ...filtros, local: e.target.value })}>
                <option value="">Todas as localizações</option>
                {dados.filtros.locais.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button onClick={() => onFiltros({ ano: '', mes: '', local: '' })}
              style={{ ...sel, minWidth: 0, color: TINTA2 }}>Limpar filtros</button>
            <button onClick={() => window.print()}
              style={{ ...sel, minWidth: 0, background: ROXO, borderColor: ROXO, color: '#fff', fontWeight: 700 }}>Imprimir / PDF</button>
          </div>
        </div>

        {aCarregar && <div style={{ fontSize: 12, color: TINTA3, marginBottom: 8 }}>A actualizar…</div>}

        <div className="wwfg-kpis">
          {kpis.map(kp => (
            <Cartao key={kp.c} style={kp.todos ? { background: '#F7F3FD', borderColor: '#D9CCEF' } : undefined}>
              <div style={{ fontSize: 38, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1.05, color: kp.rosa ? ROSA_ESC : ROXO }}>
                {nf.format(kp.v || 0)}
              </div>
              <div style={{ fontSize: 12, color: TINTA2, marginTop: 2 }}>{kp.c}</div>
            </Cartao>
          ))}
        </div>

        <div className="wwfg-g12">
          <Cartao titulo="Zonas de dor reportadas" sub="% das queixas registadas nas consultas de fisioterapia" className="wwfg-s7">
            <MapaCorporal zonas={dados.zonas || []} />
          </Cartao>
          <Cartao titulo="Zona principal de dor" sub="N.º de consultas de fisioterapia por zona" className="wwfg-s5">
            <Barras itens={(dados.zonas || []).map(z => ({ k: z.zona, v: z.sessoes }))} />
          </Cartao>
        </div>

        <div className="wwfg-g12">
          <Cartao titulo="Intensidade da dor" sub="Média na escala 0–10, início e fim da consulta de fisioterapia" className="wwfg-s4">
            <Intensidade dados={dados.intensidade} />
          </Cartao>
          <Cartao titulo="Género" sub="Distribuição das sessões · todos os serviços" className="wwfg-s4">
            <Genero dados={dados.genero || []} />
          </Cartao>
          <Cartao titulo="Escalão etário" sub="N.º de sessões por escalão · todos os serviços" className="wwfg-s4">
            <Barras itens={(dados.idade || []).map(d => ({ k: d.escalao, v: d.sessoes }))} />
          </Cartao>
        </div>

        <div className="wwfg-g12">
          {(dados.estabelecimentos || []).length > 0 && (
            <Cartao titulo="Sessões por estabelecimento" sub="Total no período · todos os serviços" className="wwfg-s5">
              <Barras itens={dados.estabelecimentos.map(d => ({ k: d.local, v: d.sessoes }))} />
            </Cartao>
          )}
          <Cartao titulo="Consultas por mês" sub="Comparação entre anos · o filtro de ano não se aplica a este gráfico"
                  className={(dados.estabelecimentos || []).length > 0 ? "wwfg-s7" : "wwfg-s12"}>
            <ConsultasPorMes mensal={dados.mensal || []} />
          </Cartao>
        </div>

        <div style={{ marginTop: 14 }}>
          <Cartao titulo="Zona de dor por mês" sub="Intensidade de cor = n.º de consultas de fisioterapia">
            <Heatmap heatmap={dados.heatmap || []} />
          </Cartao>
        </div>

        <div style={{ marginTop: 14 }}>
          <Cartao titulo="Ginástica laboral" sub="Participantes por mês">
            <Ginastica dados={dados.ginastica || []} />
          </Cartao>
        </div>


    </div>
  );
}
